import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { pathsToReload } from "../lib/review.ts";
import type { OpenFiles } from "../lib/useOpenFiles.ts";
import type { ReviewController } from "../lib/useReview.ts";
import { ReviewPane } from "./ReviewPane.tsx";

/**
 * Shows the diff review in a modal overlay, NOT in the editor slot, so reviewing never changes the panel layout (a
 * hidden Editor stays hidden) and the editor underneath is never unmounted. After an approve that applied, clean tabs
 * are reloaded from disk and the Explorer refreshes.
 */
export function ReviewHost({
  machineId,
  files,
  review,
  children,
}: {
  machineId: string;
  files: OpenFiles;
  review: ReviewController;
  children: ReactNode;
}) {
  const cs = review.openId ? review.changeSets.find((c) => c.id === review.openId) : undefined;

  const { close } = review;
  const isOpen = Boolean(cs);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  const decide = async (kind: "approve" | "reject") => {
    if (!cs) return;
    const next = await review.decide(kind, cs);
    const paths = pathsToReload(next);
    if (paths.length > 0) {
      await files.refreshFromDisk(paths);
      review.notifyApplied(paths);
    }
    close();
  };

  return (
    <div className="editor-host">
      <div className="editor-host-layer" style={{ display: "flex" }}>
        {children}
      </div>
      {cs &&
        createPortal(
          <div
            className="review-modal-backdrop"
            data-testid="review-modal"
            onMouseDown={(e) => e.target === e.currentTarget && close()}
          >
            <div className="review-modal" role="dialog" aria-modal="true" aria-label="Review changes">
              <ReviewPane
                key={cs.id}
                machineId={machineId}
                changeSet={cs}
                dirtyPaths={files.dirtyPaths}
                onApprove={() => decide("approve")}
                onReject={() => decide("reject")}
                onClose={close}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
